/**
 * UnifiedContextMenu - A unified, row-agnostic context menu system
 *
 * Consolidates mobile and desktop row action patterns into a single reusable component.
 * Eliminates code duplication by providing three trigger methods that open two menu types:
 *
 * **Trigger Methods:**
 * 1. Right-click (desktop) - Native browser contextmenu event; hold
 *    NATIVE_MENU_MODIFIER to get the browser's own menu instead
 * 2. Long-press (mobile) - Custom touch event detection via useLongPress hook
 * 3. Explicit button (both) - Via UnifiedContextMenuTrigger wrapper component
 *
 * **Menu Types:**
 * - Desktop: Radix ContextMenu (positioned near cursor)
 * - Mobile: Radix Sheet (bottom sheet)
 *
 * **Key Implementation Details:**
 * - Uses React.cloneElement to merge handlers without wrapper divs (preserves HTML semantics)
 * - Dispatches synthetic contextmenu events to unify button-click and right-click behavior
 * - Provides React Context for explicit triggers to access menu control methods
 * - Supports dynamic action labels/icons/disabled states via function props
 */

import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useRef,
  useState,
} from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useLongPress } from '@/hooks/use-long-press';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  UnifiedAction,
  UnifiedContextMenuContext,
  UnifiedContextMenuProps,
} from './types';

/**
 * Holding this while right-clicking bypasses the app menu and lets the browser
 * show its own — the same gesture Firefox uses for pages that hijack the
 * context menu.
 */
export const NATIVE_MENU_MODIFIER = 'shiftKey' as const;

// Context for explicit triggers to access menu controls
const MenuContext = createContext<UnifiedContextMenuContext | null>(null);

/**
 * UnifiedContextMenu - A row-agnostic context menu component
 *
 * Supports both desktop (right-click) and mobile (long-press) interactions.
 * The browser naturally handles mobile/desktop: right-click events only fire
 * on desktop, while long-press is detected via touch events.
 *
 * @example
 * ```tsx
 * <UnifiedContextMenu
 *   actions={[
 *     { label: 'Edit', icon: Edit, onAction: (data) => edit(data) },
 *     { type: 'separator' },
 *     { label: 'Delete', icon: Trash, onAction: (data) => del(data) },
 *   ]}
 *   data={row}
 * >
 *   <TableRow>...</TableRow>
 * </UnifiedContextMenu>
 * ```
 */
export function UnifiedContextMenu<TData>({
  actions,
  data,
  children,
  onClick,
}: UnifiedContextMenuProps<TData>) {
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const triggerRef = useRef<HTMLElement>(null);

  // Long-press handlers for mobile
  const longPressHandlers = useLongPress(
    {
      onLongPress: () => {
        setMobileSheetOpen(true);
      },
      onClick: onClick ? (event) => onClick(event, data) : undefined,
    },
    [data, onClick],
  );

  // Context value for explicit triggers (e.g., ellipsis button)
  const contextValue: UnifiedContextMenuContext = {
    openDesktopMenu: (anchorEl) => {
      if (isMobile) {
        setMobileSheetOpen(true);
      } else {
        // On desktop, programmatically trigger the ContextMenu by dispatching a synthetic
        // contextmenu event. This unifies the behavior - both right-click and button-click
        // use the same ContextMenu component, avoiding the need for a separate DropdownMenu.
        if (anchorEl || triggerRef.current) {
          const target = anchorEl || triggerRef.current!;
          const event = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            view: window,
            button: 2, // Right mouse button
            clientX: target.getBoundingClientRect().left,
            clientY: target.getBoundingClientRect().top,
          });
          target.dispatchEvent(event);
        }
      }
    },
    openMobileMenu: () => setMobileSheetOpen(true),
    anchorRef: triggerRef as React.RefObject<HTMLElement>,
  };

  // Helper to resolve dynamic label/icon/disabled
  const resolveValue = <T,>(
    value: T | ((data: TData) => T) | undefined,
  ): T | undefined => {
    if (typeof value === 'function') {
      return (value as (data: TData) => T)(data);
    }
    return value;
  };

  // Handle click events (for desktop mouse clicks)
  // useLongPress handles touch events for mobile
  const handleClick = (e: React.MouseEvent) => {
    if (onClick && e.nativeEvent instanceof MouseEvent) {
      onClick(e.nativeEvent, data);
    }
  };

  // Merge event handlers with child element's props using cloneElement.
  // This approach preserves HTML semantics (e.g., <tr> stays <tr>, no wrapper <div>)
  // which is critical for table layouts.
  const childWithHandlers = isValidElement(children)
    ? cloneElement(children as React.ReactElement<any>, {
        ref: triggerRef,
        // Add data attribute for styling (e.g., hover state when menu open)
        'data-menu-open': mobileSheetOpen ? 'true' : undefined,
        // Escape hatch to the browser's own menu, the way Firefox does it:
        // hold the modifier and the event never reaches Radix, so the native
        // menu opens instead. Matters on rows that are links — it is the only
        // way left to reach "open in new tab" / "copy link address".
        onContextMenuCapture: (e: React.MouseEvent) => {
          if (e[NATIVE_MENU_MODIFIER]) e.stopPropagation();
        },
        // Spread long-press touch handlers
        ...longPressHandlers,
        // Merge onClick handlers (call both child's and ours)
        onClick: (e: React.MouseEvent) => {
          // Call child's existing onClick if it exists
          (children as any).props?.onClick?.(e);
          // Call our navigation handler
          handleClick(e);
        },
      })
    : children;

  // Render menu items (shared between ContextMenu, DropdownMenu, and Sheet)
  const renderMenuItems = (
    onItemClick: (action: UnifiedAction<TData>) => void,
    ItemComponent: any,
    SeparatorComponent: any,
  ) => {
    return actions.map((action, idx) => {
      if (action.type === 'separator') {
        return <SeparatorComponent key={idx} />;
      }

      const label = resolveValue(action.label);
      const sublabel = resolveValue(action.sublabel);
      const IconComponent = resolveValue<LucideIcon>(
        action.icon as LucideIcon | ((data: TData) => LucideIcon) | undefined,
      );
      const disabled = resolveValue(action.disabled);

      return (
        <ItemComponent
          key={idx}
          disabled={disabled}
          onClick={(e: any) => {
            e.stopPropagation();
            onItemClick(action);
          }}
        >
          {IconComponent && (
            <IconComponent className="mr-2 h-4 w-4 flex-shrink-0" />
          )}
          <span className="flex flex-col items-start">
            <span>{label}</span>
            {sublabel && (
              <span className="text-muted-foreground italic">{sublabel}</span>
            )}
          </span>
        </ItemComponent>
      );
    });
  };

  return (
    <MenuContext.Provider value={contextValue}>
      {/* Desktop: Right-click context menu (also triggered by explicit button) */}
      <ContextMenu>
        <ContextMenuTrigger asChild>{childWithHandlers}</ContextMenuTrigger>
        <ContextMenuContent>
          {renderMenuItems(
            (action) => action.onAction?.(data),
            ContextMenuItem,
            ContextMenuSeparator,
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Mobile: Long-press bottom sheet (also triggered by explicit button) */}
      <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
        <SheetContent side="bottom" className="p-0">
          <SheetHeader className="p-4 pb-0">
            <SheetTitle>Actions</SheetTitle>
            <SheetDescription className="sr-only">
              Actions for the item you pressed and held.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-1 p-2">
            {actions.map((action, idx) => {
              if (action.type === 'separator') {
                return (
                  <div key={idx} className="border-border my-1 border-t" />
                );
              }

              const label = resolveValue(action.label);
              const sublabel = resolveValue(action.sublabel);
              const IconComponent = resolveValue<LucideIcon>(
                action.icon as
                  | LucideIcon
                  | ((data: TData) => LucideIcon)
                  | undefined,
              );
              const disabled = resolveValue(action.disabled);

              return (
                <Button
                  key={idx}
                  variant="ghost"
                  className="h-auto min-h-12 justify-start gap-3 py-2 text-base font-normal"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onAction?.(data);
                    setMobileSheetOpen(false);
                  }}
                >
                  {IconComponent && (
                    <IconComponent className="h-5 w-5 flex-shrink-0" />
                  )}
                  <span className="flex flex-col items-start">
                    <span>{label}</span>
                    {sublabel && (
                      <span className="text-muted-foreground italic">
                        {sublabel}
                      </span>
                    )}
                  </span>
                </Button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </MenuContext.Provider>
  );
}

/**
 * UnifiedContextMenuTrigger - Explicit trigger for the unified menu
 *
 * Wraps a button (like an ellipsis icon) to open the same menu as right-click/long-press.
 * Must be used inside UnifiedContextMenu.
 *
 * @example
 * ```tsx
 * <UnifiedContextMenu actions={actions} data={row}>
 *   <TableRow>...</TableRow>
 *   <UnifiedContextMenuTrigger>
 *     <Button><Ellipsis /></Button>
 *   </UnifiedContextMenuTrigger>
 * </UnifiedContextMenu>
 * ```
 */
export function UnifiedContextMenuTrigger({
  children,
  asChild = true,
}: {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const context = useContext(MenuContext);

  if (!context) {
    throw new Error(
      'UnifiedContextMenuTrigger must be used within UnifiedContextMenu',
    );
  }

  // Clone child element and add onClick handler
  if (asChild && isValidElement(children)) {
    return cloneElement(children as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        // Call child's existing onClick if it exists
        (children as any).props?.onClick?.(e);
        // Open menu with this element as anchor
        context.openDesktopMenu(e.currentTarget as HTMLElement);
      },
    });
  }

  // Fallback: render as button
  return (
    <button
      onClick={(e) => {
        context.openDesktopMenu(e.currentTarget);
      }}
    >
      {children}
    </button>
  );
}
